import { Component } from './Component';
import { World } from './World';

/**
 * Entity - A container for components
 * Entities are just IDs with a collection of components attached
 */
export class Entity {
  public readonly id: string;
  private world: World;
  private components: Map<string, Component> = new Map();
  private tags: Set<string> = new Set();

  constructor(id: string, world: World) {
    this.id = id;
    this.world = world;
  }

  /**
   * Add a component to this entity
   */
  public addComponent<T extends Component>(component: T): this {
    if (this.components.has(component.type)) {
      console.warn(`Entity ${this.id} already has component ${component.type}`);
      return this;
    }

    this.components.set(component.type, component);
    component.entity = this;
    this.world.onComponentAdded(this, component);

    return this;
  }

  /**
   * Get a component by type
   */
  public getComponent<T extends Component>(type: string): T | undefined {
    return this.components.get(type) as T | undefined;
  }

  /**
   * Check if entity has a component
   */
  public hasComponent(type: string): boolean {
    return this.components.has(type);
  }

  /**
   * Check if entity has all specified components
   */
  public hasComponents(types: string[]): boolean {
    return types.every((type) => this.components.has(type));
  }

  /**
   * Remove a component by type
   */
  public removeComponent(type: string): this {
    const component = this.components.get(type);
    if (component) {
      this.components.delete(type);
      component.entity = undefined;
      this.world.onComponentRemoved(this, component);
    }
    return this;
  }

  /**
   * Remove all components
   */
  public removeAllComponents(): void {
    for (const [_type, component] of this.components) {
      component.entity = undefined;
      this.world.onComponentRemoved(this, component);
    }
    this.components.clear();
  }

  /**
   * Get all components
   */
  public getAllComponents(): Component[] {
    return Array.from(this.components.values());
  }

  /**
   * Add a tag to this entity
   */
  public addTag(tag: string): this {
    this.tags.add(tag);
    return this;
  }

  /**
   * Remove a tag from this entity
   */
  public removeTag(tag: string): this {
    this.tags.delete(tag);
    return this;
  }

  /**
   * Check if entity has a tag
   */
  public hasTag(tag: string): boolean {
    return this.tags.has(tag);
  }

  /**
   * Get all tags
   */
  public getTags(): string[] {
    return Array.from(this.tags);
  }

  /**
   * Mark this entity for destruction
   */
  public destroy(): void {
    this.world.destroyEntity(this.id);
  }
}
